// Copyright © 2026 SHIELD Intelligence. All rights reserved.
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
  confirmPasswordReset,
  EmailAuthProvider,
  reauthenticateWithCredential,
  deleteUser,
} from "firebase/auth";
import {
  collection,
  getDocs,
  getDoc,
  deleteDoc,
  doc,
  Timestamp,
  setDoc,
  updateDoc // 👈 added
} from "firebase/firestore";
import { authenticator } from "otplib";
import { decryptWithVaultKey, encryptWithVaultKey } from "./vaultCrypto";
import { auth, db } from "./firebase";
import { getVaultAadForUser, getVaultKeyForUser, lockVault } from "./vault";
import { secureRemoveItem } from "./secureStorage";
import { vaultRememberKeyForEmail, vaultKnownKeyForEmail, offlineReadyKeyForEmail } from "./vaultLocalState";

// ---------------- Auth ----------------
export async function register(email, password) {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  return userCredential.user;
}

export async function login(email, password) {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  return userCredential.user;
}

export function logout() {
  // Ensure vault key isn't kept in memory across sessions
  lockVault();
  return signOut(auth);
}

// ---------------- Password Reset (Firebase) ----------------
export async function sendPasswordReset(email, actionCodeSettings = undefined) {
  // actionCodeSettings is optional. When provided it controls the redirect URL.
  return sendPasswordResetEmail(auth, email, actionCodeSettings);
}

export async function verifyPasswordReset(oobCode) {
  // Returns the email address for which the code is valid
  return verifyPasswordResetCode(auth, oobCode);
}

export async function confirmPasswordResetAction(oobCode, newPassword) {
  return confirmPasswordReset(auth, oobCode, newPassword);
}

// ---------------- Accounts ----------------
function requireUserEmail(user) {
  const email = user?.email;
  if (!email) throw new Error("Missing authenticated user email");
  return String(email).trim();
}

function accountDocIdFromName(name) {
  const raw = String(name || "").trim();
  if (!raw) throw new Error("Missing account name");
  // Firestore document IDs cannot contain '/'
  const id = raw.replaceAll("/", "_");
  // Reserve internal document IDs
  if (id === "__vault") throw new Error("This account name is reserved. Choose a different name.");
  return id;
}

function normalizeAccountName(name) {
  return String(name || "").trim().toLowerCase();
}

function duplicateAccountError(name) {
  const readableName = String(name || "").trim() || "this name";
  const error = new Error(`An account with name "${readableName}" already exists.`);
  error.code = "account/duplicate-name";
  return error;
}

function isPermissionDeniedError(error) {
  const code = String(error?.code || "").toLowerCase();
  const msg = String(error?.message || "").toLowerCase();
  return code === "permission-denied" || (msg.includes("permission") && msg.includes("denied"));
}

async function refreshAuthTokenForRetry() {
  try {
    const current = auth.currentUser;
    if (!current) return false;
    await current.getIdToken(true);
    return true;
  } catch {
    return false;
  }
}

async function runWithPermissionRetry(operation) {
  try {
    return await operation();
  } catch (err) {
    if (!isPermissionDeniedError(err)) throw err;

    const refreshed = await refreshAuthTokenForRetry();
    if (!refreshed) throw err;

    return operation();
  }
}

function userAccountsCollection(user) {
  const emailId = requireUserEmail(user);
  return collection(db, "user", emailId, "accounts");
}

function userAccountDocRef(user, accountId) {
  const emailId = requireUserEmail(user);
  return doc(db, "user", emailId, "accounts", accountId);
}

async function ensureNoDuplicateAccountName(user, name, excludeAccountId = null) {
  const normalizedTargetName = normalizeAccountName(name);
  if (!normalizedTargetName) throw new Error("Missing account name");

  const targetAccountId = accountDocIdFromName(name);
  if (excludeAccountId !== targetAccountId) {
    try {
      const existingById = await getDoc(userAccountDocRef(user, targetAccountId));
      if (existingById.exists()) {
        throw duplicateAccountError(name);
      }
    } catch (err) {
      // Some rulesets deny GET on documents that do not yet exist because
      // resource.data is unavailable in rule expressions. In that case,
      // defer to other duplicate checks and allow write attempt.
      if (!isPermissionDeniedError(err)) throw err;
    }
  }

  try {
    const snapshot = await getDocs(userAccountsCollection(user));
    for (const docSnap of snapshot.docs) {
      if (docSnap.id === "__vault") continue;
      if (excludeAccountId && docSnap.id === excludeAccountId) continue;

      const existingName = normalizeAccountName(docSnap.data()?.name);
      if (existingName && existingName === normalizedTargetName) {
        throw duplicateAccountError(name);
      }
    }
  } catch (err) {
    // Some Firestore rules block collection LIST while allowing per-doc access.
    // In that case we keep the strict doc-id duplicate check above and continue.
    if (!isPermissionDeniedError(err)) throw err;
  }
}

export async function addAccount(user, name, secret) {
  const accountId = accountDocIdFromName(name);
  const userId = user?.uid || "";

  await ensureNoDuplicateAccountName(user, name);

  const vaultKey = getVaultKeyForUser(user);
  const aad = getVaultAadForUser(user);
  const encryptedSecret = await encryptWithVaultKey({ plaintext: secret, vaultKey, aad });

  await runWithPermissionRetry(async () =>
    setDoc(doc(userAccountsCollection(user), accountId), {
      userId,
      name,
      secret: encryptedSecret,
      createdAt: Timestamp.now(),
    })
  );
}

export async function deleteAccount(user, accountId) {
  if (accountId === "__vault") throw new Error("Invalid account id");
  await runWithPermissionRetry(async () => deleteDoc(userAccountDocRef(user, accountId)));
}

export async function getAccounts(user) {
  const snapshot = await getDocs(userAccountsCollection(user));
  const results = [];

  const vaultKey = getVaultKeyForUser(user);
  const aad = getVaultAadForUser(user);

  for (const docSnap of snapshot.docs) {
    if (docSnap.id === "__vault") continue;
    const data = docSnap.data();

    let plaintext = "";
    try {
      plaintext = await decryptWithVaultKey({ ciphertext: data.secret, vaultKey, aad });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("Failed to decrypt account secret for doc", docSnap.id, e);
      plaintext = "";
    }

    results.push({ id: docSnap.id, ...data, secret: plaintext });
  }
  return results;
}

export async function updateAccount(user, accountId, { name, secret }) {
  if (accountId === "__vault") throw new Error("Invalid account id");
  const accountRef = userAccountDocRef(user, accountId);
  const updates = {};

  const hasNewName = typeof name === "string" && name.trim().length > 0;
  const newAccountId = hasNewName ? accountDocIdFromName(name) : accountId;

  if (hasNewName) {
    await ensureNoDuplicateAccountName(user, name, accountId);
  }

  // If name changes, also move the document so the path stays:
  // user/{email}/accounts/{Accountname:username}
  if (hasNewName && newAccountId !== accountId) {
    const snap = await getDoc(accountRef);
    if (!snap.exists()) throw new Error("Account not found");

    const existing = snap.data();
    const merged = {
      ...existing,
      name,
      updatedAt: Timestamp.now(),
    };
    if (secret) {
      const vaultKey = getVaultKeyForUser(user);
      const aad = getVaultAadForUser(user);
      merged.secret = await encryptWithVaultKey({ plaintext: secret, vaultKey, aad });
    }

    await runWithPermissionRetry(async () => setDoc(userAccountDocRef(user, newAccountId), merged));
    await runWithPermissionRetry(async () => deleteDoc(accountRef));
    return newAccountId;
  }

  if (hasNewName) updates.name = name;
  if (secret) {
    const vaultKey = getVaultKeyForUser(user);
    const aad = getVaultAadForUser(user);
    updates.secret = await encryptWithVaultKey({ plaintext: secret, vaultKey, aad });
  }
  updates.updatedAt = Timestamp.now();
  await runWithPermissionRetry(async () => updateDoc(accountRef, updates));
  return accountId;
}

// ---------------- OTP ----------------
export function getCode(secret) {
  return authenticator.generate(secret);
}

export function getCountdown() {
  return 30 - Math.floor(Date.now() / 1000) % 30;
}

// ---------------- Delete Account ----------------
export async function deleteUserAccount(user, password) {
  if (!user) throw new Error("No authenticated user");

  const emailId = requireUserEmail(user);
  const credential = EmailAuthProvider.credential(user.email, password);
  await reauthenticateWithCredential(user, credential);

  const accountsRef = userAccountsCollection(user);
  const snapshot = await getDocs(accountsRef);
  const deleteOps = snapshot.docs.map((docSnap) =>
    deleteDoc(doc(db, "user", emailId, "accounts", docSnap.id))
  );
  await Promise.all(deleteOps);

  clearAllLocalVaultState(emailId);
  await deleteUser(user);
}

function clearAllLocalVaultState(email) {
  const normalized = String(email || "").trim().toLowerCase();
  if (!normalized) return;

  try { localStorage.removeItem(`shield-vault-meta:${normalized}`); } catch {}
  try { localStorage.removeItem(vaultKnownKeyForEmail(normalized)); } catch {}
  try { localStorage.removeItem(offlineReadyKeyForEmail(normalized)); } catch {}
  try { secureRemoveItem(vaultRememberKeyForEmail(normalized)); } catch {}
}
